import { useState, useEffect } from 'react';
import { Sheet } from '../shared/Sheet';
import { Icon } from '../chrome/Icon';
import { useT } from '../../i18n';
import { useAuth } from '../../auth/AuthProvider';
import { authMode } from '../../auth/firebase';

interface Props {
  open: boolean;
  initialMode: 'email' | 'password';
  onClose: () => void;
}

export function AccountSettingsSheet({ open, initialMode, onClose }: Props) {
  const t = useT();
  const auth = useAuth();

  const [mode, setMode] = useState<'email' | 'password'>(initialMode);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState(false);

  // Sync mode with initialMode when opening
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setCurrentPassword('');
      setNewEmail('');
      setNewPassword('');
      setConfirmPassword('');
      setErrorMsg(null);
      setSuccess(false);
      setTouched(false);
      auth.clearError();
    }
  }, [open, initialMode]);

  // Validation
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim());
  const isPasswordValid = newPassword.length >= 6;
  const isPasswordMatch = newPassword === confirmPassword;
  const hasCurrentPassword = authMode === 'mock' || currentPassword.length > 0;

  const formValid =
    mode === 'email'
      ? isEmailValid && hasCurrentPassword
      : isPasswordValid && isPasswordMatch && hasCurrentPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setErrorMsg(null);

    if (!formValid || submitting) return;

    setSubmitting(true);
    auth.clearError();

    try {
      if (mode === 'email') {
        await auth.changeEmail(currentPassword, newEmail.trim());
      } else {
        await auth.changePassword(currentPassword, newPassword);
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (e: any) {
      console.error('Account settings update failed:', e);
      setErrorMsg(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      size="md"
      maxHeight="92dvh"
      label={
        mode === 'email'
          ? t('profile.changeEmail', 'Change Email')
          : t('profile.changePassword', 'Change Password')
      }
    >
      <div className="flex flex-col gap-6 px-6 pb-8 pt-0 overflow-y-auto">
        {/* Close Row */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.accountSettings', 'Account Settings')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in">
            <div className="flex size-16 items-center justify-center rounded-full bg-[#dff7e6] text-[#1a7f37] mb-4">
              <Icon name="check" size={32} color="currentColor" />
            </div>
            <h2 className="font-serif text-[24px] leading-tight text-ink mb-2">
              {t('common.success', 'Success!')}
            </h2>
            <p className="text-[14.5px] text-ink-2 max-w-[280px]">
              {mode === 'email'
                ? t(
                    'profile.emailUpdateSuccess',
                    'Your email has been updated. A verification link has been sent to your new email address.'
                  )
                : t('profile.passwordUpdateSuccess', 'Your password has been successfully updated.')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
            {/* Mode Tabs */}
            <div className="inline-flex rounded-[10px] bg-grey-soft p-0.5 self-start">
              <button
                type="button"
                onClick={() => {
                  setMode('email');
                  setErrorMsg(null);
                  auth.clearError();
                }}
                className={[
                  'min-h-9 rounded-lg px-4 text-[13px] transition-all cursor-pointer',
                  mode === 'email'
                    ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(33,28,21,0.08)]'
                    : 'bg-transparent text-ink-2 font-medium',
                ].join(' ')}
              >
                {t('profile.email', 'Email')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('password');
                  setErrorMsg(null);
                  auth.clearError();
                }}
                className={[
                  'min-h-9 rounded-lg px-4 text-[13px] transition-all cursor-pointer',
                  mode === 'password'
                    ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(33,28,21,0.08)]'
                    : 'bg-transparent text-ink-2 font-medium',
                ].join(' ')}
              >
                {t('profile.password', 'Password')}
              </button>
            </div>

            <h1 className="font-serif text-[28px] leading-tight tracking-tight text-ink mt-1">
              {mode === 'email'
                ? t('profile.changeEmailTitle', 'Update your email')
                : t('profile.changePasswordTitle', 'Change your password')}
            </h1>
            <p className="text-[14.5px] leading-relaxed text-ink-2 text-pretty -mt-2">
              {mode === 'email'
                ? t(
                    'profile.changeEmailDesc',
                    'Update the email address you use to sign in and receive notifications.'
                  )
                : t(
                    'profile.changePasswordDesc',
                    'Choose a secure new password to keep your account safe.'
                  )}
            </p>

            {/* Current Password - Required for re-auth (real auth only) */}
            {authMode !== 'mock' && (
              <div>
                <label
                  htmlFor="acct-curr-pass"
                  className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3"
                >
                  {t('profile.currentPassword', 'Current Password')}
                </label>
                <input
                  id="acct-curr-pass"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className={[
                    'mt-2.5 w-full rounded-xl border bg-surface px-4 py-3 text-[15px] text-ink outline-none transition-colors',
                    touched && !hasCurrentPassword
                      ? 'border-[#B5523B]'
                      : 'border-line focus:border-ink',
                  ].join(' ')}
                />
                {touched && !hasCurrentPassword && (
                  <p className="mt-1.5 text-[12px] text-[#7E3320]">
                    {t('profile.currentPasswordRequired', 'Current password is required to save changes.')}
                  </p>
                )}
              </div>
            )}

            {mode === 'email' ? (
              /* Email Fields */
              <div>
                <label
                  htmlFor="acct-new-email"
                  className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3"
                >
                  {t('profile.newEmail', 'New Email Address')}
                </label>
                <input
                  id="acct-new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you-new@example.com"
                  autoComplete="email"
                  className={[
                    'mt-2.5 w-full rounded-xl border bg-surface px-4 py-3 text-[15px] text-ink outline-none transition-colors',
                    touched && !isEmailValid ? 'border-[#B5523B]' : 'border-line focus:border-ink',
                  ].join(' ')}
                />
                {touched && !isEmailValid && (
                  <p className="mt-1.5 text-[12px] text-[#7E3320]">
                    {t('profile.invalidEmail', 'Please enter a valid email address.')}
                  </p>
                )}
              </div>
            ) : (
              /* Password Fields */
              <>
                <div>
                  <label
                    htmlFor="acct-new-pass"
                    className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3"
                  >
                    {t('profile.newPassword', 'New Password')}
                  </label>
                  <input
                    id="acct-new-pass"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('profile.atLeast6', 'At least 6 characters')}
                    autoComplete="new-password"
                    className={[
                      'mt-2.5 w-full rounded-xl border bg-surface px-4 py-3 text-[15px] text-ink outline-none transition-colors',
                      touched && !isPasswordValid
                        ? 'border-[#B5523B]'
                        : 'border-line focus:border-ink',
                    ].join(' ')}
                  />
                  {touched && !isPasswordValid && (
                    <p className="mt-1.5 text-[12px] text-[#7E3320]">
                      {t('profile.invalidPassword', 'Password must be at least 6 characters.')}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="acct-conf-pass"
                    className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3"
                  >
                    {t('profile.confirmPassword', 'Confirm New Password')}
                  </label>
                  <input
                    id="acct-conf-pass"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className={[
                      'mt-2.5 w-full rounded-xl border bg-surface px-4 py-3 text-[15px] text-ink outline-none transition-colors',
                      touched && (!isPasswordMatch || (touched && !confirmPassword))
                        ? 'border-[#B5523B]'
                        : 'border-line focus:border-ink',
                    ].join(' ')}
                  />
                  {touched && isPasswordValid && !isPasswordMatch && (
                    <p className="mt-1.5 text-[12px] text-[#7E3320]">
                      {t('profile.passwordsMustMatch', 'Passwords do not match.')}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Error alerts */}
            {(auth.error || errorMsg) && (
              <div
                role="alert"
                className="rounded-xl px-4 py-3 text-[13px] leading-snug flex items-start gap-2"
                style={{
                  background: 'rgba(181, 82, 59, 0.10)',
                  border: '1px solid rgba(181, 82, 59, 0.22)',
                  color: '#7E3320',
                }}
              >
                <span className="shrink-0">⚠️</span>
                <span>{auth.error || errorMsg}</span>
              </div>
            )}

            {/* Submit CTA */}
            <button
              type="submit"
              disabled={submitting}
              className="
                mt-2 inline-flex w-full min-h-14 items-center justify-center gap-2.5
                rounded-3xl bg-ink px-6 text-[15.5px] font-semibold text-bg
                disabled:opacity-50 hover:bg-ink/90 active:scale-[0.98] transition-all cursor-pointer
              "
            >
              {submitting ? (
                <span className="w-5 h-5 border-2 border-bg border-t-transparent animate-spin rounded-full" />
              ) : mode === 'email' ? (
                t('profile.updateEmailCTA', 'Update Email')
              ) : (
                t('profile.updatePasswordCTA', 'Update Password')
              )}
              {!submitting && <Icon name="arrow" size={18} color="currentColor" />}
            </button>
          </form>
        )}
      </div>
    </Sheet>
  );
}
